import { faTrashCan as DeleteIcon } from '@fortawesome/pro-light-svg-icons';
import {
  faBan as DisableIcon,
  faCheck as EnableIcon
} from '@fortawesome/pro-regular-svg-icons';
import { faEllipsisVertical as MoreIcon } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Typography from '@mui/material/Typography';
import { useAtom } from 'jotai';
import {
  bindMenu,
  bindTrigger,
  usePopupState
} from 'material-ui-popup-state/hooks';

import { PERMISSIONS } from '@/constants/auth.constants';
import useConfirm from '@/hooks/useConfirm';
import usePermissionsCheck from '@/hooks/usePermissionsCheck';
import { useFetchSettings } from '@/pages/Administration/Administration.store';
import {
  ticketAtom,
  useDeleteTicket,
  useUpdateAutoCloseEnabled
} from '@/pages/Ticket/Ticket.store';

const TicketDetailsMoreButton = () => {
  const [{ id: ticket_id, auto_close_enabled }] = useAtom(ticketAtom);
  const { data: orgSettings } = useFetchSettings();
  const canDeleteTicket = usePermissionsCheck(PERMISSIONS.TICKETS_DELETE);
  const canManageAutoClose = usePermissionsCheck([PERMISSIONS.TICKETS_UPDATE]);
  const canToggleAutoClose =
    Boolean(orgSettings?.auto_close_enabled) && canManageAutoClose;
  const hasVisibleActions = canDeleteTicket || canToggleAutoClose;
  const deleteTicket = useDeleteTicket();
  const updateAutoCloseEnabled = useUpdateAutoCloseEnabled();
  const popupState = usePopupState({
    variant: 'popover',
    popupId: 'moreMenu'
  });

  const { ConfirmationModal: TicketDeleteModal, confirm: confirmDelete } =
    useConfirm({
      title: 'Delete Ticket',
      message: 'Please confirm that you want to delete this ticket.',
      button: 'Delete'
    });

  const handleDelete = async () => {
    const confirmed = await confirmDelete();
    if (confirmed && ticket_id !== undefined) {
      deleteTicket.mutate();
    }
    popupState.close();
  };

  const handleToggleAutoClose = () => {
    updateAutoCloseEnabled.mutate(!auto_close_enabled);
    popupState.close();
  };

  if (!hasVisibleActions) {
    return null;
  }

  return (
    <>
      <Button color="primary" variant="outlined" {...bindTrigger(popupState)}>
        <FontAwesomeIcon icon={MoreIcon} />
      </Button>
      <Menu {...bindMenu(popupState)}>
        {canToggleAutoClose && (
          <MenuItem onClick={handleToggleAutoClose}>
            <ListItemIcon>
              <Typography>
                <FontAwesomeIcon
                  icon={auto_close_enabled ? DisableIcon : EnableIcon}
                  size="lg"
                />
              </Typography>
            </ListItemIcon>
            <ListItemText>
              <Typography>
                {auto_close_enabled
                  ? 'Disable Auto-Close'
                  : 'Enable Auto-Close'}
              </Typography>
            </ListItemText>
          </MenuItem>
        )}
        {canToggleAutoClose && canDeleteTicket && <Divider />}
        {canDeleteTicket && (
          <MenuItem onClick={handleDelete}>
            <ListItemIcon>
              <Typography sx={{ color: 'error.main' }}>
                <FontAwesomeIcon icon={DeleteIcon} size="lg" />
              </Typography>
            </ListItemIcon>
            <ListItemText>
              <Typography sx={{ color: 'error.main' }}>Delete</Typography>
            </ListItemText>
          </MenuItem>
        )}
      </Menu>
      <TicketDeleteModal />
    </>
  );
};

export default TicketDetailsMoreButton;
